import { IContext } from "@src/context";
import duDatepicker from "@dmuy/datepicker";
import { TLocale } from "@src/i18n";
import { fetchProperty } from "./fetchProperty";
import { generatePdf } from "./generatePdf";
import messages from "./messages";

import "./index.css";

// Types & Config

interface RoomFeatures {
  accAvl1?: number;
  accAvl2?: number;
  accAvl3?: number;
  accAvl4?: number;
  vs?: boolean;
  unPVs?: boolean;
  hwVsGas?: boolean;
  unHwVsGas?: boolean;
  hwVsElec?: boolean;
  unHwVsElec?: boolean;
  kwha?: number;
  gs?: boolean;
  tvIc?: boolean;
  wl?: boolean;
  unWl?: boolean;
  pet?: boolean;
  petRent?: number;
  pk?: boolean;
  twoPk?: boolean;
  depAmo?: number;
  cashBack?: boolean;
  catvFee?: number;
  waterFee?: number;
  sewaFee?: number;
  utilFee?: number;
  hwVsSet?: string;
}

const OPTIONS: Record<
  string,
  { price: number; available: (f: RoomFeatures, pid?: number) => boolean }
> = {
  "air-conditioner": { price: 1100, available: (f) => getMaxAC(f) > 0 },
  "washbasin-bowl": { price: 1100, available: (f) => !f.vs && !f.unPVs },
  "washbasin-hot-water-supply": {
    price: 1100,
    available: (f) =>
      (!f.hwVsGas && !f.unHwVsGas) || (!f.hwVsElec && !f.unHwVsElec),
  },
  "kitchen-water-heater": {
    price: 1100,
    available: (f, pid) => f.kwha === 1 && pid !== 1,
  },
  "gas-stove": { price: 1100, available: (f) => !f.gs },
  "tv-intercom": { price: 600, available: (f) => !f.tvIc },
  "bidet-heated": { price: 1100, available: (f) => !f.wl && !f.unWl },
};

const DEPOSIT_MONTHS: Record<string, number> = {
  "welfare-assistance": 2,
  "corporate-contract": 2,
  "pet-ownership": 1,
};

const getMaxAC = (f?: RoomFeatures) =>
  f
    ? (f.accAvl1 || 0) + (f.accAvl2 || 0) + (f.accAvl3 || 0) + (f.accAvl4 || 0)
    : 0;

// InitialCostForm

class InitialCostForm {
  private container: HTMLElement;
  private locale: TLocale;
  private costElements = new Map<string, HTMLElement>();
  private roomRent = 0;
  private petRent = 0;
  private maxACCount = 4;
  private parkingFee = 0;
  private hasDeposit = false;
  private hasCashBack = false;
  private currentFeat?: RoomFeatures;
  private currentPid?: number;

  constructor(container: HTMLElement, locale: TLocale) {
    this.container = container;
    this.locale = locale;
    this.init();
  }

  private init() {
    this.container
      .querySelectorAll<HTMLElement>("[data-initial-cost-name]")
      .forEach((el) => {
        if (el.dataset.initialCostName)
          this.costElements.set(el.dataset.initialCostName, el);
      });

    const dateInput = this.getInput<HTMLInputElement>("move-in-date");
    if (dateInput) {
      duDatepicker(dateInput, {
        theme: "minimal",
        clearBtn: false,
        format: "yyyy/mm/dd",
        i18n: this.locale,
        minDate: "today",
        range: false,
        dateChanged: () => this.calculate(),
      });
    }

    this.container.addEventListener("change", (e) => {
      const target = e.target as HTMLElement;
      if (target.matches("input, select")) {
        const name = (target as HTMLInputElement).name;
        if (name === "air-conditioner") this.syncACRadios();
        if (name === "welfare-assistance" || name === "corporate-contract")
          this.syncDepositCheckboxes();
        if (name === "washbasin-hot-water-supply") this.syncWashbasinOptions();
        this.calculate();
      }
    });

    this.reset();
  }

  private getInput<T extends HTMLElement>(name: string): T | null {
    return this.container.querySelector<T>(`[name="${name}"]`);
  }

  private getInputs(name: string): HTMLInputElement[] {
    return Array.from(
      this.container.querySelectorAll<HTMLInputElement>(`[name="${name}"]`)
    );
  }

  private isChecked(name: string): boolean {
    return this.getInput<HTMLInputElement>(name)?.checked ?? false;
  }

  private getRadioValue(name: string): string {
    return this.getInputs(name).find((r) => r.checked)?.value || "";
  }

  private getCostValue(name: string): number {
    return parseInt(this.costElements.get(name)?.dataset.value || "0", 10) || 0;
  }

  private setCostValue(name: string, value: number) {
    const element = this.costElements.get(name);
    if (element) {
      element.dataset.value = String(value);
      element.textContent = new Intl.NumberFormat(this.locale, {
        maximumFractionDigits: 0,
      }).format(value);
    }
  }

  private sumCosts(...names: string[]): number {
    return names.reduce((sum, name) => sum + this.getCostValue(name), 0);
  }

  private prorate(amount: number): number {
    const dateStr = this.getInput<HTMLInputElement>("move-in-date")?.value;
    if (!dateStr) return 0;
    // 2026/01/01
    const [year, month, day] = dateStr.split("/").map(Number);
    if (!year || !month || !day) return 0;
    if (day === 1) return amount;
    const daysInMonth = new Date(year, month, 0).getDate();
    const remainingDays = daysInMonth - day + 1;
    return Math.round((amount / 30) * remainingDays);
  }

  private calculate() {
    // ① Monthly Rent
    const hasPet = this.isChecked("pet-ownership");
    const monthlyRent = this.roomRent + (hasPet ? this.petRent : 0);
    this.setCostValue("monthly-rent", monthlyRent);

    // ③ Additional Options
    let optionsTotal = 0;
    const feat = this.currentFeat;
    for (const [name, { price }] of Object.entries(OPTIONS)) {
      let value = 0;
      if (this.isChecked(name)) {
        if (name === "air-conditioner") {
          value =
            (parseInt(this.getRadioValue("air-conditioner-count"), 10) || 1) *
            price;
        } else if (name === "washbasin-hot-water-supply") {
          const isSet = feat?.hwVsSet === "セット設置可";
          value = isSet && feat?.vs ? price * 2 : price;
        } else {
          value = price;
        }
      }
      this.setCostValue(name, value);
      optionsTotal += value;
    }
    this.setCostValue("additional-options", optionsTotal);

    // ⑤ Parking Space Fee
    const parkingCount =
      parseInt(
        this.getInput<HTMLSelectElement>("parking-fee")?.value || "0",
        10
      ) || 0;
    const parkingFee = parkingCount * this.parkingFee;
    this.setCostValue("parking-fee", parkingFee);

    // ⑬ Security Deposit
    const hasWelfareOrCorporate =
      this.isChecked("welfare-assistance") ||
      this.isChecked("corporate-contract");
    let depositMonths = 0;
    if (hasWelfareOrCorporate) {
      depositMonths = 2;
    } else if (this.hasDeposit) {
      depositMonths = 1;
    }
    if (hasPet) depositMonths += 1;
    const depositBase = monthlyRent + optionsTotal;
    this.setCostValue("deposit-months", depositMonths);
    this.setCostValue("deposit-amount", depositBase * depositMonths);

    // ⑭ Fire Insurance
    this.setCostValue(
      "fire-insurance",
      parseInt(this.getRadioValue("fire-insurance"), 10) || 0
    );

    // ⑮ Moving Support
    const movingSupport = this.hasCashBack
      ? -Math.min(monthlyRent + optionsTotal, 30000)
      : 0;
    this.setCostValue("moving-support", movingSupport);

    // ⑧⑨⑩⑪ Prorated
    const maintenanceFee = this.getCostValue("maintenance-fee");
    const proratedRent = this.prorate(monthlyRent + optionsTotal);
    const proratedMaintenance = this.prorate(maintenanceFee);
    // CATV/water/sewer are full month, utilities are prorated daily.
    const monthlyCharges = this.sumCosts(
      "catv-usage-fee",
      "water-charges",
      "sewer-charges"
    );
    const proratedUtility = this.prorate(this.getCostValue("utility-fees"));
    const proratedOther = monthlyCharges + proratedUtility;
    const proratedParking = this.prorate(parkingFee);
    this.setCostValue("prorated-rent", proratedRent);
    this.setCostValue("prorated-maintenance-fee", proratedMaintenance);
    this.setCostValue("other-rental-charges", proratedOther);
    this.setCostValue("prorated-parking-fee", proratedParking);

    // Subtotals & Total
    const basicSubtotal = this.sumCosts(
      "monthly-rent",
      "maintenance-fee",
      "additional-options",
      "catv-usage-fee",
      "water-charges",
      "sewer-charges",
      "utility-fees",
      "parking-fee"
    );
    const variableSubtotal =
      proratedRent + proratedMaintenance + proratedOther + proratedParking;
    const otherSubtotal =
      this.getCostValue("deposit-amount") +
      this.getCostValue("fire-insurance") +
      this.getCostValue("moving-support");

    this.setCostValue("basic-initial-cost-subtotal", basicSubtotal);
    this.setCostValue("variable-initial-cost-subtotal", variableSubtotal);
    this.setCostValue("other-fees-subtotal", otherSubtotal);
    this.setCostValue(
      "initial-costs-total",
      basicSubtotal + variableSubtotal + otherSubtotal
    );
  }

  private syncACRadios() {
    const checked = this.isChecked("air-conditioner");
    this.getInputs("air-conditioner-count").forEach((radio) => {
      radio.disabled = !checked || parseInt(radio.value, 10) > this.maxACCount;
    });
  }

  private syncDepositCheckboxes() {
    const welfare = this.getInput<HTMLInputElement>("welfare-assistance");
    const corporate = this.getInput<HTMLInputElement>("corporate-contract");
    if (welfare && corporate) {
      welfare.disabled = corporate.checked;
      corporate.disabled = welfare.checked;
    }
  }

  private syncWashbasinOptions() {
    const hotWater = this.getInput<HTMLInputElement>(
      "washbasin-hot-water-supply"
    );
    const bowl = this.getInput<HTMLInputElement>("washbasin-bowl");
    if (!hotWater || !bowl) return;
    const feat = this.currentFeat;
    const isSet = feat?.hwVsSet === "セット設置可";
    const bowlAvailable = feat && OPTIONS["washbasin-bowl"].available(feat);
    // only link when hwVsSet=セット設置可 and bowl is available
    if (!isSet || !bowlAvailable) return;
    if (hotWater.checked) {
      bowl.checked = true;
      bowl.disabled = true;
    } else {
      bowl.disabled = false;
    }
  }

  private syncOptionsState() {
    const feat = this.currentFeat;
    const pid = this.currentPid;

    const parkingSelect = this.getInput<HTMLSelectElement>("parking-fee");
    if (parkingSelect) {
      const maxPk = feat?.pk ? (feat?.twoPk ? 2 : 1) : 0;
      parkingSelect.innerHTML = Array.from(
        { length: maxPk + 1 },
        (_, i) => `<option value="${i}">${i}</option>`
      ).join("");
      parkingSelect.disabled = !feat?.pk;
      parkingSelect.value = "0";
    }

    const petInput = this.getInput<HTMLInputElement>("pet-ownership");
    if (petInput) {
      petInput.disabled = !feat?.pet;
      if (!feat?.pet) petInput.checked = false;
    }

    for (const [name, { available }] of Object.entries(OPTIONS)) {
      const input = this.getInput<HTMLInputElement>(name);
      if (input && feat && !available(feat, pid)) {
        input.checked = false;
        input.disabled = true;
      }
    }
  }

  reset() {
    for (const name of Object.keys(OPTIONS)) {
      const input = this.getInput<HTMLInputElement>(name);
      if (input) {
        input.checked = false;
        input.disabled = false;
      }
    }

    for (const name of Object.keys(DEPOSIT_MONTHS)) {
      const input = this.getInput<HTMLInputElement>(name);
      if (input) {
        input.checked = false;
        input.disabled = false;
      }
    }

    this.getInputs("air-conditioner-count").forEach((radio) => {
      radio.checked = radio.value === "1";
      radio.disabled = true;
    });

    this.getInputs("fire-insurance").forEach((radio) => {
      radio.checked = radio.value === "10000";
    });

    const parkingSelect = this.getInput<HTMLSelectElement>("parking-fee");
    if (parkingSelect) parkingSelect.value = "0";

    const dateInput = this.getInput<HTMLInputElement>("move-in-date");
    if (dateInput) {
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 7);
      const y = defaultDate.getFullYear();
      const m = String(defaultDate.getMonth() + 1).padStart(2, "0");
      const d = String(defaultDate.getDate()).padStart(2, "0");
      duDatepicker(dateInput, "setValue", `${y}/${m}/${d}`);
    }

    this.syncOptionsState();
    this.syncACRadios();
    this.calculate();
  }

  applyRoomData(pid: number, rent: number, pkFee: number, feat?: RoomFeatures) {
    this.currentFeat = feat;
    this.currentPid = pid;
    this.roomRent = rent;
    this.petRent = feat?.petRent || 0;
    this.parkingFee = pkFee;
    this.maxACCount = getMaxAC(feat);
    this.hasDeposit = (feat?.depAmo ?? 0) > 0;
    this.hasCashBack = feat?.cashBack || false;

    this.setCostValue("monthly-rent", rent);
    this.setCostValue("maintenance-fee", 0);
    this.setCostValue("catv-usage-fee", feat?.catvFee || 0);
    this.setCostValue("water-charges", feat?.waterFee || 0);
    this.setCostValue("sewer-charges", feat?.sewaFee || 0);
    this.setCostValue("utility-fees", feat?.utilFee || 0);

    this.syncOptionsState();
    this.syncACRadios();
    this.calculate();
  }

  setTitle(title: string) {
    const titleEl = this.container.querySelector<HTMLElement>(
      ".container-initial-cost-heading-title"
    );
    if (titleEl) titleEl.textContent = title;

    const remarkEl = this.container.querySelector<HTMLElement>(
      ".container-initial-cost-heading-remark"
    );
    if (remarkEl) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      remarkEl.textContent = `${year}/${month}/${day}`;
    }
  }

  syncCurrencyDisplay() {
    this.costElements.forEach((element) => {
      const value = parseInt(element.dataset.value || "0", 10) || 0;
      element.textContent = new Intl.NumberFormat(this.locale, {
        maximumFractionDigits: 0,
        numberingSystem: "latn",
      }).format(value);
    });
  }

  showLoading() {
    const content = this.container.querySelector<HTMLElement>(
      ".container-initial-cost-content"
    );
    if (content && !content.querySelector(".element-loading")) {
      content.insertAdjacentHTML(
        "afterbegin",
        '<div class="element-loading"></div>'
      );
    }
  }

  hideLoading() {
    this.container.querySelector(".element-loading")?.remove();
  }
}

// Entry Point

export function setupInitialCost(context: IContext) {
  const container = document.querySelector<HTMLElement>(
    ".container-initial-cost"
  );
  if (!container) return;

  const form = new InitialCostForm(container, context.locale);

  container
    .querySelector(".container-initial-cost-reset")
    ?.addEventListener("click", () => form.reset());

  container
    .querySelector(".container-initial-cost-pdf")
    ?.addEventListener("click", () => generatePdf(context, container));

  document
    .querySelectorAll<HTMLElement>(
      '[data-popup-id="initial-cost"][data-popup-event="popup-open"]'
    )
    .forEach((trigger) => {
      trigger.addEventListener("click", async () => {
        const { propertyId, roomId } = trigger.dataset;
        if (!propertyId || !roomId) return;

        form.showLoading();
        try {
          const property = await fetchProperty(propertyId, context.locale);
          const room = property?.rooms?.find((r) => r.id === roomId);
          if (!property || !room) return;

          form.setTitle(
            context.intl.formatMessage(messages.RoomName, {
              name: property.name,
            }) +
              context.intl.formatMessage(messages.RoomNumber, {
                bNo: room.bNo,
                rNo: room.rNo,
              })
          );
          form.reset();
          form.applyRoomData(
            property.pid ?? 0,
            room.rent ?? 0,
            property.pkFee ?? 0,
            room.feat as RoomFeatures
          );
          form.syncCurrencyDisplay();
        } catch (error) {
          console.error("Failed to fetch property:", error);
        } finally {
          form.hideLoading();
        }
      });
    });
}
